/**
 * Session storage utilities for encrypted tenant report data
 */

interface StoredData {
  data: any;
  timestamp: number;
  expiresAt: number;
  email: string;
  token: string;
}

/**
 * Cache duration in milliseconds (30 minutes)
 */
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

/**
 * Storage key prefix for tenant report data
 */
const STORAGE_KEY_PREFIX = "multiburo_tenant_report_";

/**
 * Generates a unique storage key for the report data
 */
const generateStorageKey = (token: string, email: string): string => {
  // Create a simple hash-like identifier from token and email
  const identifier = btoa(`${token}_${email}`)
    .replace(/[^a-zA-Z0-9]/g, "")
    .substring(0, 16);
  return `${STORAGE_KEY_PREFIX}${identifier}`;
};

/**
 * Saves decrypted tenant report data to session storage with 30-minute expiration
 * @param token - Report token
 * @param email - User email
 * @param data - Decrypted report data
 */
export const saveReportDataToSession = (
  token: string,
  email: string,
  data: any,
): void => {
  try {
    const now = Date.now();
    const storageKey = generateStorageKey(token, email);

    const storedData: StoredData = {
      data,
      timestamp: now,
      expiresAt: now + CACHE_DURATION,
      email,
      token,
    };

    sessionStorage.setItem(storageKey, JSON.stringify(storedData));
  } catch (error) {
    console.warn("Failed to save report data to session storage:", error);
    // Don't throw error - this is an optimization, not critical
  }
};

/**
 * Retrieves cached tenant report data from session storage
 * @param token - Report token
 * @param email - User email
 * @returns Cached data if valid and not expired, null otherwise
 */
export const getReportDataFromSession = (
  token: string,
  email: string,
): any | null => {
  try {
    const storageKey = generateStorageKey(token, email);
    const storedString = sessionStorage.getItem(storageKey);

    if (!storedString) {
      return null;
    }

    const storedData: StoredData = JSON.parse(storedString);
    const now = Date.now();

    // Check if data has expired
    if (now > storedData.expiresAt) {
      sessionStorage.removeItem(storageKey);
      return null;
    }

    // Validate that the cached data matches the requested token and email
    if (storedData.token !== token || storedData.email !== email) {
      console.warn("Cached data token/email mismatch, removing from storage");
      sessionStorage.removeItem(storageKey);
      return null;
    }

    const remainingTime = Math.round((storedData.expiresAt - now) / 1000 / 60);

    return storedData.data;
  } catch (error) {
    console.warn("Failed to retrieve report data from session storage:", error);
    return null;
  }
};

/**
 * Removes cached report data from session storage
 * @param token - Report token
 * @param email - User email
 */
export const removeReportDataFromSession = (
  token: string,
  email: string,
): void => {
  try {
    const storageKey = generateStorageKey(token, email);
    sessionStorage.removeItem(storageKey);
  } catch (error) {
    console.warn("Failed to remove report data from session storage:", error);
  }
};

/**
 * Clears all expired cached report data from session storage
 */
export const clearExpiredReportData = (): void => {
  try {
    const now = Date.now();
    const keysToRemove: string[] = [];

    // Iterate through all session storage items
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);

      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        try {
          const storedString = sessionStorage.getItem(key);
          if (storedString) {
            const storedData: StoredData = JSON.parse(storedString);

            // Mark expired items for removal
            if (now > storedData.expiresAt) {
              keysToRemove.push(key);
            }
          }
        } catch (error) {
          // If we can't parse the data, it's corrupted - mark for removal
          keysToRemove.push(key);
        }
      }
    }

    // Remove expired items
    keysToRemove.forEach((key) => {
      sessionStorage.removeItem(key);
    });
  } catch (error) {
    console.warn("Failed to clear expired report data:", error);
  }
};

/**
 * Gets information about cached data without returning the actual data
 * @param token - Report token
 * @param email - User email
 * @returns Cache info or null if not cached
 */
export const getReportCacheInfo = (
  token: string,
  email: string,
): {
  exists: boolean;
  expiresAt?: number;
  remainingMinutes?: number;
} => {
  try {
    const storageKey = generateStorageKey(token, email);
    const storedString = sessionStorage.getItem(storageKey);

    if (!storedString) {
      return { exists: false };
    }

    const storedData: StoredData = JSON.parse(storedString);
    const now = Date.now();

    if (now > storedData.expiresAt) {
      return { exists: false };
    }

    const remainingMinutes = Math.round(
      (storedData.expiresAt - now) / 1000 / 60,
    );

    return {
      exists: true,
      expiresAt: storedData.expiresAt,
      remainingMinutes,
    };
  } catch (error) {
    return { exists: false };
  }
};

/**
 * Checks if there's any cached data for a given token (regardless of email)
 * @param token - Report token
 * @returns Object with cache info if found, null otherwise
 */
export const findCachedDataForToken = (
  token: string,
): {
  data: any;
  email: string;
  remainingMinutes: number;
} | null => {
  try {
    const now = Date.now();

    // Iterate through all session storage items
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);

      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        try {
          const storedString = sessionStorage.getItem(key);
          if (storedString) {
            const storedData: StoredData = JSON.parse(storedString);

            // Check if this is for our token and not expired
            if (storedData.token === token && now <= storedData.expiresAt) {
              const remainingMinutes = Math.round(
                (storedData.expiresAt - now) / 1000 / 60,
              );

              return {
                data: storedData.data,
                email: storedData.email,
                remainingMinutes,
              };
            }
          }
        } catch (error) {
          // Skip corrupted entries
          continue;
        }
      }
    }

    return null;
  } catch (error) {
    console.warn("Failed to search for cached data:", error);
    return null;
  }
};

/**
 * Initialize session storage cleanup
 * Clears expired data when the module is loaded
 */
export const initializeSessionStorage = (): void => {
  // Clear expired data on initialization
  clearExpiredReportData();

  // Set up periodic cleanup every 5 minutes
  setInterval(clearExpiredReportData, 5 * 60 * 1000);
};
